import React, { ReactNode, FC } from 'react';

import { theme, colors, ThemeLanguageOverride } from '@lucid/theme';
import { ThemeProvider } from '@lucid/components-react';

import ABTest, { Variation } from 'components/ab-test/ab-test';
import ABTestBranch from 'components/ab-test-branch/ab-test-branch';

interface ThemeABProviderProps {
  children: ReactNode;
  langPrefix: string;
}

const i18nCollapseAt: ThemeLanguageOverride = {
  de: '1135px',
  fr: '1185px',
  ja: '1170px',
  'pt-br': '1055px',
  es: '1125px',
  nl: '1158px'
};

const baseVariantShared = {
  borderRadius: '5px',
  outline: 'none'
};

const buttons = {
  primary: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'orange.45',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'orange.45',
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'orange.80',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'orange.45',
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'orange.80',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'orange.45'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'orange.80',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'orange.45'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'orange.45',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'orange.45',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondary: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'orange.45'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'orange.45'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  primaryAlternate: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'indigo.60',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'indigo.60',
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'indigo.90',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'indigo.90',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolgrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'indigo.60',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondaryAlternate: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  }
};

const ThemeABProvider: FC<ThemeABProviderProps>  = ({ children, langPrefix }) => {
  const customTheme = Object.assign({}, theme);
  const alternativeTheme = Object.assign({}, theme);
  customTheme.navbar = theme.navbar;
  // @ts-ignore
  alternativeTheme.buttons = buttons;

  if (langPrefix !== 'en' && i18nCollapseAt[langPrefix]) {
    customTheme.navbar.collapseAt = i18nCollapseAt[langPrefix];
    alternativeTheme.navbar.collapseAt = i18nCollapseAt[langPrefix];
  }

  return (
    <ABTest aboveTheFold test="Y21Q1_ThemeButtonColorSwap" trigger="load">
      <ABTestBranch branch={Variation.A}>
        <ThemeProvider
          product="chart"
          theme={customTheme}
        >
          {children}
        </ThemeProvider>
      </ABTestBranch>
      <ABTestBranch branch={Variation.B}>
        <ThemeProvider
          product="chart"
          theme={alternativeTheme}
        >
          {children}
        </ThemeProvider>
      </ABTestBranch>
    </ABTest>
  );
};

export default ThemeABProvider;
